import { Component, ElementRef, HostListener, Inject, PLATFORM_ID, Input, OnDestroy, OnInit, Output, EventEmitter} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientReservationService } from '../../../_services/api/reservation-client.service';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { OfferWithActions, Agent, CustomerOffer } from '../../../_models/reservation.model';
import { OfferUtilsService } from '../../../_utils/offer-utils.service';
import { PriceFormatPipe } from '../../../_pipes/price_format.pipe';
import { trigger, state, style, transition, animate } from '@angular/animations';


@Component({
  selector: 'app-member-reservation-offer-card',
  imports: [CommonModule, FormsModule, PriceFormatPipe],
  templateUrl: './member-reservation-offer-card.html',
  styleUrl: './member-reservation-offer-card.scss',
  animations: [
    trigger('slideInOut', [
      state('open', style({
        height: '*',
        opacity: 1,
        overflow: 'visible'
      })),
      state('closed', style({
        height: '0',
        opacity: 0,
        overflow: 'hidden'
      })),
      transition('open <=> closed', [
        animate('300ms ease-in-out')
      ])
    ]),
    trigger('rotateArrow', [
      state('open', style({
        transform: 'rotate(180deg)'
      })),
      state('closed', style({
        transform: 'rotate(0deg)'
      })),
      transition('open <=> closed', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class MemberReservationOfferCard implements OnInit, OnDestroy {
  @Input() offer!: OfferWithActions;
  @Input() reservationId!: number;
  @Input() agentsExpanded: boolean = true;

  @Output() offerAccepted = new EventEmitter<void>();
  @Output() offerRejected = new EventEmitter<void>();

  menuOpen = false;
  showDeleteModal = false;
  showCancelModal = false;
  showRejectModal = false;
  isAgentsExpanded = false;
  isPerformingAction = false;
  actionType: 'accept' | 'reject' | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ClientReservationService,
    private offerUtils: OfferUtilsService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    this.isAgentsExpanded = this.agentsExpanded;
    this.menuStateService.activeMenu$.subscribe(activeId => {
      this.menuOpen = activeId === this.offer.id;
    });
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('click', this.closeMenu.bind(this));
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  // Offer Action Methods

  acceptOffer(): void {
    if (this.offer.status !== 'PENDING') {
      this.notification.showError('Seules les offres en attente peuvent être acceptées');
      return;
    }

    this.actionType = 'accept';
    this.menuOpen = false;
    this.isPerformingAction = true;

    this.reservationService.acceptOffer(this.reservationId, this.offer.id)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Offre acceptée avec succès');
          this.offerAccepted.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de l\'acceptation de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  rejectOffer(): void {
    if (this.offer.status !== 'PENDING') {
      this.notification.showError('Seules les offres en attente peuvent être refusées');
      return;
    }

    this.actionType = 'reject';
    this.menuOpen = false;
    this.showRejectModal = true;
  }

  confirmRejectOffer(): void {
    this.isPerformingAction = true;
    this.showRejectModal = false;

    this.reservationService.rejectOffer(this.reservationId, this.offer.id)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Offre refusée avec succès');
          this.offerRejected.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors du refus de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  // Modal close methods
  closeRejectModal(): void {
    this.showRejectModal = false;
    this.actionType = null;
  }

  // UI Methods
  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.offer.id);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  toggleAgentsList(event: Event): void {
    event.stopPropagation();
    this.isAgentsExpanded = !this.isAgentsExpanded;
  }

  getOfferStatusClass(): string {
    return this.offerUtils.getOfferStatusClass(this.offer.status as any);
  }

  getOfferStatusText(): string {
    return this.offerUtils.getOfferStatusText(this.offer.status as any);
  }

  canAcceptOffer(): boolean {
    return this.offer.status === 'PENDING';
  }

  canRejectOffer(): boolean {
    return this.offer.status === 'PENDING';
  }

  getActionButtonText(action: string): string {
    switch (action) {
      case 'accept': return 'Accepter';
      case 'reject': return 'Refuser';
      default: return action;
    }
  }

  getLoadingText(): string {
    switch (this.actionType) {
      case 'accept': return 'Acceptation en cours...';
      case 'reject': return 'Refus en cours...';
      default: return 'Traitement en cours...';
    }
  }
}